/**
 * Durable local copy of this player's DCC store, per room.
 *
 * WHY: OBR player metadata does not survive a reload. Confirmed live on
 * 2026-09-10: a crawler vanished on F5, on closing and reopening the tab, and
 * on rejoining the next day. Our code never writes on load, so the loss is
 * OBR dropping player metadata when the connection is recreated.
 *
 * HOW: this wraps the ObrPlayerApi seam. Every writer (the StoragePort adapter
 * AND the visibility service) already goes through `setMetadata`, so mirroring
 * here catches all of them with no changes above this line.
 *
 *   write:   OBR metadata first, then browser storage. If OBR rejects, the
 *            local copy is untouched.
 *   startup: `restore()`. If OBR metadata already holds our store (panel
 *            reopened mid-session), it is the live copy and wins; the local
 *            copy is synced to it. If OBR metadata is empty (after a reload),
 *            the local copy is pushed back into OBR metadata so the GM's
 *            party view sees it again.
 *
 * KEY: one record per room, `com.lordofdisndat.dcc/v1/room/<roomId>`, holding
 * the store whose characters are indexed by character id. The logical key is
 * therefore (room id, character id), the same pair the planned Worker + D1
 * layer will use. Each campaign has its own OBR room, so one room = one set of
 * sheets.
 *
 * LIMITS (by design, until the Worker + D1 slice): browser storage is per
 * device and per browser, and is lost if the player clears site data. Using
 * two tabs in the same room at once is unsupported: the last tab to write
 * wins the local copy.
 *
 * FAILURE POLICY: never silent. Storage blocked, full, or unreadable sets a
 * status the UI shows as a warning. A failed LOCAL write does not throw,
 * because the OBR write already succeeded and the change is live; throwing
 * would make the UI report a failed action that actually happened.
 */
import type { Metadata, ObrPlayerApi, PlayerRole } from './obrSdk.ts';
import { DCC_METADATA_KEY } from './obrStorage.ts';

/** The slice of the Web Storage API we use. `window.localStorage` fits it. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type MirrorStatus =
  /** Local copy is in step with the live sheet. */
  | 'ok'
  /** This browser blocks storage for the extension; nothing survives a reload. */
  | 'unavailable'
  /** A local write failed (storage full or blocked mid-session). */
  | 'failed'
  /** The saved local copy could not be read; a backup of it was kept. */
  | 'corrupt'
  /**
   * A saved copy exists but could not be loaded back into OBR. Local writes
   * are then frozen for the session, so a fresh edit on an apparently empty
   * sheet can never overwrite the saved one. Reloading retries.
   */
  | 'restore-failed';

export function localStoreKey(roomId: string): string {
  return `com.lordofdisndat.dcc/v1/room/${roomId}`;
}

/** Where an unreadable local copy is moved, so it is never silently lost. */
export function corruptBackupKey(roomId: string): string {
  return `${localStoreKey(roomId)}/corrupt-backup`;
}

/**
 * `window.localStorage`, or null when the browser refuses it. Some browsers
 * throw on mere access inside a third-party iframe, and some allow access but
 * throw on write, so both are probed.
 */
export function detectBrowserStorage(
  getStorage: () => KeyValueStore | undefined = () => globalThis.localStorage,
): KeyValueStore | null {
  try {
    const storage = getStorage();
    if (!storage) return null;
    const probe = `${DCC_METADATA_KEY}/probe`;
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

function isStoreObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export class LocalMirrorPlayer implements ObrPlayerApi {
  private status: MirrorStatus;
  private listeners = new Set<(s: MirrorStatus) => void>();
  private frozen = false;

  constructor(
    private readonly inner: ObrPlayerApi,
    private readonly local: KeyValueStore | null,
    private readonly roomId: string,
  ) {
    this.status = local ? 'ok' : 'unavailable';
  }

  get id(): string { return this.inner.id; }
  getRole(): Promise<PlayerRole> { return this.inner.getRole(); }
  getName(): Promise<string> { return this.inner.getName(); }
  getMetadata(): Promise<Metadata> { return this.inner.getMetadata(); }

  async setMetadata(update: Partial<Metadata>): Promise<void> {
    await this.inner.setMetadata(update);
    // Own-property check, not `in` (which also walks the prototype chain).
    if (Object.prototype.hasOwnProperty.call(update, DCC_METADATA_KEY)) {
      this.writeLocal(update[DCC_METADATA_KEY]);
    }
  }

  /** Run once at startup, before anything reads the store. */
  async restore(): Promise<void> {
    const meta = await this.inner.getMetadata();
    const live = meta[DCC_METADATA_KEY];
    if (isStoreObject(live)) {
      // Mid-session reopen: OBR holds the live copy. Bring local in step.
      this.writeLocal(live);
      return;
    }
    const saved = this.readLocal();
    if (saved === null) return;
    try {
      await this.inner.setMetadata({ [DCC_METADATA_KEY]: saved });
    } catch {
      this.frozen = true;
      this.setStatus('restore-failed');
    }
  }

  getStatus(): MirrorStatus { return this.status; }

  onStatusChange(cb: (s: MirrorStatus) => void): () => void {
    this.listeners.add(cb);
    return () => { this.listeners.delete(cb); };
  }

  private setStatus(next: MirrorStatus): void {
    if (next === this.status) return;
    this.status = next;
    for (const cb of this.listeners) cb(next);
  }

  private writeLocal(store: unknown): void {
    if (!this.local) return; // status is already 'unavailable'
    if (this.frozen) return; // protect the saved copy; status says why
    try {
      this.local.setItem(localStoreKey(this.roomId), JSON.stringify(store));
      // A successful write clears a previous failure, but a corrupt-backup
      // warning stays visible for the rest of the session.
      if (this.status === 'failed') this.setStatus('ok');
    } catch {
      this.setStatus('failed');
    }
  }

  /** The saved store, or null if there is none or it is unreadable. */
  private readLocal(): Record<string, unknown> | null {
    if (!this.local) return null;
    let raw: string | null;
    try {
      raw = this.local.getItem(localStoreKey(this.roomId));
    } catch {
      this.setStatus('failed');
      return null;
    }
    if (raw === null) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (isStoreObject(parsed)) return parsed;
    } catch {
      // fall through to the corrupt path
    }
    this.keepCorruptBackup(raw);
    return null;
  }

  private keepCorruptBackup(raw: string): void {
    if (!this.local) return;
    try {
      this.local.setItem(corruptBackupKey(this.roomId), raw);
    } catch {
      // Even the backup failed; the warning still shows.
    }
    this.setStatus('corrupt');
  }
}
